import React, { lazy, Suspense, useEffect, useState, Fragment } from 'react'
import { withRouter } from 'react-router-dom'
import { Layout, Spin, Row, Button, Alert } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../helpers/appDispatch'
import { MerchantData } from '../interfaces'
import {
  getMerchantsRequest,
  exportViewPCESReportsRequest,
  viewPCESReportsRequest
} from '../store/reports'
import moment from 'moment'
import { isEmpty } from '../helpers/isEmpty'
import { useTranslation } from 'react-i18next'

const { Content } = Layout

const Filters = lazy(() => import('../components/fee-reports/Filters'))
const Cards = lazy(() => import('../components/fee-reports/Cards'))
const Details = lazy(() => import('../components/fee-reports/Details'))
const CurrencyFilter = lazy(() => import('../components/fee-reports/CurrencyFilter'))
const EmptyBox = lazy(() => import('../components/fee-reports/EmptyBox'))

const FeeReports = () => {
  const dispatch: AppDispatch = useDispatch()
  const { t } = useTranslation()
  const { merchants, pces, loading, isExporting, pcesError, pcesExportError } = useSelector(
    (state: any) => state.reports
  )
  const pcesData = pces?.records || pces?.Records || pces?.merchantSummaryRecords || pces?.MerchantSummaryRecords || []
  const pcesSummary = pces?.summary || pces?.Summary || null
  const pcesTotal = pcesSummary
    ? pcesSummary.recordsTotal ??
      pcesSummary.RecordsTotal ??
      pcesSummary.merchantTotal ??
      pcesSummary.MerchantTotal ??
      pcesData.length
    : pcesData.length
  const pcesReport = pces || null
  const [merchant, setMerchant] = useState<MerchantData | null>(null)
  const [periodFrom, setPeriodFrom] = useState('')
  const [periodTo, setPeriodTo] = useState('')
  const [exportType, setExportType] = useState('')
  const [pageSize, setPageSize] = useState(10)
  const [skip, setSkip] = useState(0)
  const [currency, setCurrency] = useState('USD')
  const [isSearching, setIsSearching] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const getErrorMessage = (error: any) => {
    if (typeof error === 'string') return error
    return error?.detail || error?.message || error?.title || JSON.stringify(error)
  }

  useEffect(() => {
    if (isEmpty(merchants)) {
      dispatch(getMerchantsRequest())
    }
  }, [])


  const reloadReport = () => {
    dispatch(viewPCESReportsRequest({
      currency,
      pageSize,
      skip,
      periodFrom,
      periodTo,
      merchant: merchant?.name || '',
      exportType: '',
      status: 'APPROVED',
      fixedPeriod: 'overall'
    }))
  }

  const onSelectCurrency = (value: string) => {
    setCurrency(value)
    setSkip(0)
    setCurrentPage(1)

    dispatch(viewPCESReportsRequest({
      currency: value,   
      pageSize,
      skip: 0,
      periodFrom,
      periodTo,
      merchant: merchant?.name || '',
      exportType: '',
      status: 'APPROVED',
      fixedPeriod: 'overall'
    }))
  }

  const onLoadMore = (page: number, size: number) => {
    const newSkip = page - 1
    setPageSize(size)
    setSkip(newSkip)
    setCurrentPage(page)

    dispatch(viewPCESReportsRequest({
      currency,
      pageSize: size,       
      skip: newSkip,
      periodFrom,
      periodTo,
      merchant: merchant?.name || '',
      exportType: '',
      status: 'APPROVED',
      fixedPeriod: 'overall'
    }))
  }

  const onSearch = (values: any) => {
    setIsSearching(true)
    setSkip(0)
    setCurrentPage(1)

    const { merchant: merchantId, periodFrom: pFromRaw, periodTo: pToRaw } = values

    let pFrom = ''
    let pTo = ''
    let m: MerchantData | undefined = undefined

    if (!isEmpty(pFromRaw) && !isEmpty(pToRaw)) {
      pFrom = moment(pFromRaw).format('MM/DD/YYYY')
      pTo = moment(pToRaw).format('MM/DD/YYYY')
      setPeriodFrom(pFrom)
      setPeriodTo(pTo)
    }

    if (!isEmpty(merchantId)) {
      m = merchants.find((item: MerchantData) => item.merchantId === merchantId)
      setMerchant(m !== undefined ? m : null)
    } else {
      setMerchant(null)
    }

    dispatch(viewPCESReportsRequest({
      currency,
      pageSize,
      skip: 0,
      periodFrom: pFrom,    
      periodTo: pTo,        
      merchant: m?.name || '',  
      exportType: '',
      status: 'APPROVED',
      fixedPeriod: 'overall'
    }))
  }

  const onReset = (form: any) => {
    form.resetFields()
    setPeriodFrom('')
    setPeriodTo('')
    setMerchant(null)
    setPageSize(10)
    setSkip(0)
    setCurrentPage(1)
    setIsSearching(false)

    dispatch(viewPCESReportsRequest({
      currency,
      pageSize: 10,         
      skip: 0,
      periodFrom: '',       
      periodTo: '',         
      merchant: '',         
      exportType: '',
      status: 'APPROVED',
      fixedPeriod: 'overall'
    }))
  }

  const onExportClick = (type: string) => {
    setExportType(type)

    dispatch(exportViewPCESReportsRequest({
      currency,
      pageSize,
      skip: 0,
      periodFrom,
      periodTo,
      merchant: merchant?.name || '',
      exportType: type,     
      status: 'APPROVED',
      fixedPeriod: 'overall'
    }))
  }

  return (
    <div className="padding-box">
      <Content className="site-layout-background site-box">
        <Suspense
          fallback={
            <Row className="suspense-container">
              <div style={{ marginTop: '200px' }}>
                <Spin />
              </div>
            </Row>
          }
        >
          <Fragment>
            <Filters
              onReset={onReset}
              onSearch={onSearch}
              merchants={merchants}
              translate={t}
            />

            {isSearching && pcesError && (
              <Alert
                message="Failed to fetch data"
                description={getErrorMessage(pcesError)}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {isSearching && pcesExportError && (
              <Alert
                message="Failed to export data"
                description={getErrorMessage(pcesExportError)}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {isSearching ? (
              <Fragment>
                <Cards
                  pces={pcesData}
                  pcesReport={pcesReport}
                  currency={currency}
                  loading={loading}
                  translate={t}
                />
                <CurrencyFilter
                  onSelectCurrency={onSelectCurrency}
                  translate={t}
                />
                <div className="margin-top">
                  <Row style={{ position: 'relative' }}>
                    <h4 className="transaction-chart-text">
                      {t('general.FEEReports')}
                    </h4>
                    <div className="utility-buttons">
                      <Button
                        type="primary"
                        className="export-buttons"
                        onClick={() => onExportClick('EXCEL')}
                        loading={isExporting && exportType === 'EXCEL'}
                        disabled={isEmpty(pcesData)}
                      >
                        {t('general.export-excel')}
                      </Button>
                      <Button
                        type="primary"
                        className="export-buttons reload"
                        onClick={reloadReport}
                      >
                        {t('general.refresh')}
                      </Button>
                    </div>
                  </Row>

                  {loading ? (
                    <Row className="suspense-container">
                      <div style={{ marginTop: '80px' }}>
                        <Spin size="large" />
                      </div>
                    </Row>
                  ) : (
                  <Details
                    pces={pcesData}
                    currency={currency}
                    loading={loading}
                    onLoadMore={onLoadMore}
                      total={pcesTotal}
                    translate={t}
                    currentPage={currentPage}
                  />
                  )}
                </div>
              </Fragment>
            ) : (
              <EmptyBox header={t('general.filterToGetData')} description="" />
            )}
          </Fragment>
        </Suspense>
      </Content>
    </div>
  )
}

export default withRouter(FeeReports)
